import { PLPFilterProductClass } from "./types";

const ATTRIBUTE_PREFIX = "attr_";

// Filters that only mean something inside the category they were picked in.
// Colors are the clearest case: a lens has no frame, so a frame color carried
// over from sunglasses empties the list. The price bounds come from the
// previous category's own range and say nothing about the next one.
const CATEGORY_SCOPED_KEYS = ["fc", "lc", "minp", "maxp"];
const COLOR_FAMILY_PREFIX = "cf_";

/**
 * Drops the filters the target category cannot answer for.
 *
 * Every filter used to survive a category switch, so picking a category that
 * shares none of them landed on an empty result with no hint of why. What
 * stays is what is known to still be valid: the attributes the target owns,
 * plus the filters that are not tied to a category at all (sort, brand,
 * search, discount and stock).
 *
 * Attributes get the precise treatment because the response says which ones
 * each category owns. The rest are dropped outright, since nothing in the
 * response tells us whether they survive the move.
 *
 * A category without `attribute_ids` keeps its attributes, which is what the
 * site did before the field existed. An empty list is different: it means the
 * category owns no filterable attribute, so all of them go.
 */
export function pruneParamsForCategory(
	params: URLSearchParams,
	target: PLPFilterProductClass | undefined,
): URLSearchParams {
	CATEGORY_SCOPED_KEYS.forEach((key) => params.delete(key));
	// Colour families too: the parts differ between categories (an accessory has no frame).
	for (const name of Array.from(params.keys())) {
		if (name.startsWith(COLOR_FAMILY_PREFIX)) params.delete(name);
	}

	if (!target?.attribute_ids) return params;

	const allowed = new Set(target.attribute_ids.map((id) => String(id)));

	for (const name of Array.from(params.keys())) {
		if (!name.startsWith(ATTRIBUTE_PREFIX)) continue;
		if (!allowed.has(name.slice(ATTRIBUTE_PREFIX.length))) params.delete(name);
	}

	return params;
}
